import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/inquiries`;
const TYPES = ["Trade Show", "Retail Program", "Space / Environment", "Object / Prop", "Something Else"];

const inputCls =
  "w-full border border-ink/20 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/35 transition-colors duration-300 focus:border-ink focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-form-success" className="border border-ink/15 bg-white p-10">
        <p className="font-display text-2xl font-bold uppercase tracking-tight text-ink">
          Brief received.
        </p>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/70">
          Thanks — we've received your brief and we'll get back to you shortly. Need us sooner?{" "}
          <a href={`mailto:${SITE.email}`} className="underline underline-offset-4">
            {SITE.email}
          </a>{" "}
          ·{" "}
          <a href={SITE.phoneHref} className="underline underline-offset-4">
            {SITE.phone}
          </a>
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="border border-ink/15 bg-white p-6 sm:p-10"
    >
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={labelCls}>
            Name *
          </label>
          <input
            id="contact-name"
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={set("name")}
            className={inputCls}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="contact-company" className={labelCls}>
            Brand / Company
          </label>
          <input
            id="contact-company"
            data-testid="contact-company-input"
            value={form.company}
            onChange={set("company")}
            className={inputCls}
            placeholder="Brand or company"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelCls}>
            Email *
          </label>
          <input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            className={inputCls}
            placeholder="you@brand.com"
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className={labelCls}>
            Phone
          </label>
          <input
            id="contact-phone"
            data-testid="contact-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            className={inputCls}
            placeholder="(___) ___-____"
          />
        </div>
      </div>

      <div className="mt-6">
        <span className={labelCls}>Project Type</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Project type">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`contact-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setForm((f) => ({ ...f, project_type: t }))}
              aria-pressed={form.project_type === t}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                form.project_type === t
                  ? "border-ink bg-ink text-paper"
                  : "border-ink/20 text-ink/60 hover:border-ink hover:text-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <label htmlFor="contact-message" className={labelCls}>
          The Project *
        </label>
        <textarea
          id="contact-message"
          data-testid="contact-message-input"
          required
          rows={5}
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} resize-y`}
          placeholder="Show, space, timeline, budget ballpark — whatever you have."
        />
      </div>

      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {status === "error" && (
        <p
          data-testid="contact-form-error"
          className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-red-700"
        >
          Something went wrong — email us at {SITE.email}
        </p>
      )}

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "sending"}
        className="group mt-8 flex w-full items-center justify-center gap-3 bg-ink px-8 py-5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-uv disabled:opacity-60 sm:w-auto"
      >
        {status === "sending" ? "Sending…" : "Send the Brief"}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </button>
    </form>
  );
}
